import { useState, useEffect, useMemo } from "react";
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Components
import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import ShiftLog from "examples/ShiftLog";
import theme from "assets/theme";

// Routes
// import routes from "routes";\
import routes from "routes";
import managerroute from "managerroute";
import userroutes from "userroutes";
import super_adminroutes from "super_adminroutes";
import admin_routes from "admin_routes";
import noRoleRoutes from "noRoleRoutes";

// Layout imports
import SiteMap from "layouts/sitemaps";
import Profile from "layouts/profile";
import Local_Link from "layouts/local_link";
// import Units from "layouts/units";
import Calendar from "layouts/Calendar";
import Library from "layouts/library";
import EventLog from "layouts/eventlog";
import Instructions from "layouts/instructions";
import Incidents from "layouts/incident";
import Resident_Directory from "layouts/resident_directory";
import Reservations from "layouts/reservations";
import PetRegistry from "layouts/petregistry";
import Email from "layouts/email";
import Maintenance from "layouts/maintenance";
import searchRequests from "layouts/searchRequests";
import Manage from "layouts/manage";
import FrontDesk from "layouts/FrontDesk";
import Communicate from "layouts/communicate";
import ResidentSite from "layouts/residentsite";
import Other from "layouts/other";
import Tables from "layouts/vendor-directory";
import Public_Display from "layouts/public_display";
import BuildingDirectory from "layouts/building_directory";
import Property from "layouts/manager/property";
import Organization from "layouts/admin/Organization-detail/Organization";

// Auth
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ForgetPassword from "layouts/authentication/forget";
import PrivateRoute from "./PrivateRoute";
import { AuthProvider } from 'context/AuthContext';
import { useAuth } from 'context/AuthContext';

import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";
import brand from "assets/images/logo-ct.png";
import RolesAssignWarningPage from "layouts/RolesAssignWarningPAge";
import OrganizationMain from "layouts/admin/Organization/OrganizationMain";
// import UnitDetail from "layouts/admin/Unit-detail/UnitDetail";

// Admin's imports`
import AdminPropertiesDetail from "layouts/test-admin/Properties-detail/AdminPropertyDetail";
import AdminUnitDetail from "layouts/test-admin/Unit-detail/AdminUnitDetail";
import PropertyDetail from "layouts/admin/Property-detail/PropertyDetail";
import UnitDetail from "layouts/admin/Unit-detail/UnitDetail";
import { HierarchyProvider } from "context/HierarchyContext";
import AdminProperties from "layouts/test-admin/Properties-detail/Property/AdminProperties";
import AccessControl from "layouts/AccessControl";
import UserDashboard from "layouts/dashboard/UserDashboard";
import yetToCome from "utils/yetToCome/yetToCome";

// ============ MAIN APP COMPONENT ============
function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <HierarchyProvider>
          <CssBaseline />
          <AppRoutes />
        </HierarchyProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

// ============ APP ROUTES COMPONENT ============
function AppRoutes() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  // Get auth state from context
  const { 
    isAuthenticated, 
    isAdmin, 
    isSuperAdmin,
    isManager, 
    isStaff, 
    isUser, 
    loading ,
    hasRole,
  } = useAuth();

  // User config based on role
const userConfig = useMemo(() => {
  if (isSuperAdmin) return {
    routes: super_adminroutes,
    brandName: "HG Pro | Super Admin",
    defaultPath: "/super-admin/organization",
    showShiftLog: true,
    showSupport: false
  };
  if (isAdmin) return {
    routes: admin_routes,
    brandName: "HG Pro | Admin",
    defaultPath: "/admin/properties",
    showShiftLog: true,
    showSupport: true
  };
  if (isManager) return {
    routes: managerroute,
    brandName: "HG Pro | Manager",
    defaultPath: "/manager/properties",
    showShiftLog: true,
    showSupport: true
  };
  if (isStaff) return {
    routes: routes,
    brandName: "HG Pro | Staff",
    defaultPath: "/concierge/unit",
    showShiftLog: true,
    showSupport: true
  };
  if (isUser) return {
    routes: userroutes,
    brandName: "HG Pro | User",
    defaultPath: "/user/properties",
    showShiftLog: false,
    showSupport: true
  };

  // Fallback when logged in but no role
  if (isAuthenticated && !hasRole)
    return {
      routes: noRoleRoutes,
      brandName: "HG Pro",
      defaultPath: "/role-not-assigned",
      showShiftLog: false,
      showSupport: false,
    };
  return null;
}, [isSuperAdmin, isAdmin, isManager, isStaff, isUser, isAuthenticated, hasRole]);

  // Sidenav handlers
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  // Show loading
  if (loading) {
    return (
      <SoftBox 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        Loading...
      </SoftBox>
    );
  }

  const configsButton = (
    <SoftBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      title="Support"
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        support_agent
      </Icon>
    </SoftBox>
  );

  // Get default path for redirects
  const getDefaultPath = () => {
    if (!hasRole && isAuthenticated) return "/role-not-assigned";
    if (isSuperAdmin) return "/super-admin/organization";
    if (isAdmin) return "/admin/properties";
    if (isManager) return "/manager/properties";
    if (isStaff) return "/concierge/unit";
    if (isUser) return "/user/dashboard";
    return "/sign-in";
  };

  const isUnitTab = pathname.includes("unit");

  return (
    <>
      {/* Sidenav - only when authenticated */}
      {isAuthenticated && layout === "dashboard" && userConfig && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName={userConfig.brandName}
            routes={userConfig.routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {userConfig.showShiftLog && <ShiftLog />}
          {userConfig?.showSupport && isUnitTab && configsButton}
        </>
      )}

      <Routes>
        <Route path="/admin/dashboard" element={<PrivateRoute element={UserDashboard} />} />
        <Route path="/manager/dashboard" element={<PrivateRoute element={UserDashboard} />} />
        <Route path="/user/dashboard" element={<PrivateRoute element={UserDashboard} />} />
        <Route path="/super-admin/dashboard" element={<PrivateRoute element={UserDashboard} />} />
        <Route path="/concierge/dashboard" element={<PrivateRoute element={UserDashboard} />} />
        <Route
          path="/sign-in"
          element={isAuthenticated ? <Navigate to={getDefaultPath()} replace /> : <SignIn />}
        />
        <Route
          path="/sign-up"
          element={isAuthenticated ? <Navigate to={getDefaultPath()} replace /> : <SignUp />}
        />
        <Route
          path="/forget-password"
          element={
            isAuthenticated ? <Navigate to={getDefaultPath()} replace /> : <ForgetPassword />
          }
        />

        <Route
          path="/role-not-assigned"
          element={
            isAuthenticated && !hasRole ? (
              <RolesAssignWarningPage />
            ) : (
              <Navigate to={getDefaultPath()} replace />
            )
          }
        />
        <Route path="/profile" element={<PrivateRoute element={Profile} />} />

        {/* ============ ADMIN ROUTES ============ */}
        {isSuperAdmin && (
          <>
            <Route
              path="/super-admin/:orgTab?"
              element={<PrivateRoute element={OrganizationMain} />}
            />
            <Route
              path="/super-admin/organization/:orgCode/:orgDetailTab?"
              element={<PrivateRoute element={Organization} />}
            />
            <Route
              path="/super-admin/organization/:orgCode/properties/:propertyCode/:propertyDetailTab?"
              element={<PrivateRoute element={PropertyDetail} />}
            />
            <Route
              path="/super-admin/organization/:orgCode/properties/:propertyCode/unit/:unitCode/:unitDetailTab?"
              element={<PrivateRoute element={UnitDetail} />}
            />
          </>
        )}

        {isAdmin && (
          <>
            <Route
              path="/admin/:propertyDetailTab?"
              element={<PrivateRoute element={AdminPropertiesDetail} />}
            />
            <Route
              path="/admin/properties/:propertyCode/:unitDetailTab?"
              element={<PrivateRoute element={AdminUnitDetail} />}
            />
            <Route
              path="/admin/access-control"
              element={<PrivateRoute element={AccessControl} />}
            />
          </>
        )}

        {/* ============ MANAGER ROUTES ============ */}
        {isManager && (
          <>
            <Route
              path="/manager/:propertyDetailTab?"
              element={<PrivateRoute element={AdminPropertiesDetail} />}
            />
            <Route
              path="/manager/properties/:propertyCode/:unitDetailTab?"
              element={<PrivateRoute element={AdminUnitDetail} />}
            />
            <Route path="/manager/manage" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/unit-occupants" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/calendar" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/library" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/reservations" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/pet-registry" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/incidents" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/instructions" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/event-log" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/manager/front-desk" element={<PrivateRoute element={yetToCome} />} />
          </>
        )}

        {/* ============ STAFF ROUTES ============ */}
        {isStaff && (
          <>
            {/* <Route path="/concierge/properties" element={<PrivateRoute element={AdminProperties} />} />
            <Route path="/concierge/properties/:propertyCode/:unitDetailTab?" element={<PrivateRoute element={AdminUnitDetail} />} /> */}
            {/* <Route path="/concierge/unit" element={<PrivateRoute element={AdminProperties} />} /> */}
            <Route
              path="/concierge/unit/:unitDetailTab?"
              element={<PrivateRoute element={AdminUnitDetail} />}
            />
            <Route path="/concierge/communicate" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/maintenance" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/front-desk" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/manage" element={<PrivateRoute element={yetToCome} />} />
            <Route
              path="/concierge/unit-occupants"
              element={<PrivateRoute element={yetToCome} />}
            />
            <Route path="/concierge/calendar" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/library" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/reservations" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/pet-registry" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/incidents" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/instructions" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/event-log" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/new-request" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/search-requests" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/vendor-directory" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/send-email" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/library" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/public-display" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/resident-directory" element={<PrivateRoute element={yetToCome} />} />
            <Route path="/concierge/building-directory" element={<PrivateRoute element={yetToCome} />} />
          </>
        )}

        {/* ============ USER ROUTES ============ */}
        {isUser && (
          <>
            <Route path="/user/properties" element={<PrivateRoute element={AdminProperties} />} />
            <Route
              path="/user/properties/:propertyCode/:unitDetailTab?"
              element={<PrivateRoute element={AdminUnitDetail} />}
            />
            <Route path="/user/profile" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/calendar" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/library" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/reservations" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/pet-registry" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/maintenance/new/request" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/maintenance/search/requests" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/maintenance/vendor/directory" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/event/log" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/instructions" element={<PrivateRoute element={yetToCome} />}/>
            <Route path="/user/incidents" element={<PrivateRoute element={yetToCome} />} />

            {/* Communication */}
            <Route
              path="/user/communicate/send-email"
              element={<PrivateRoute element={yetToCome} />}
            />

            {/* Yet to come routes */}
            <Route
              path="/user/billing"
              element={<PrivateRoute element={yetToCome} />}
            />

            <Route
              path="/user/virtual-reality"
              element={<PrivateRoute element={yetToCome} />}
            />

            {/* <Route path="/user/dashboard" element={<PrivateRoute element={Profile} />} />
            <Route path="/user/profile" element={<PrivateRoute element={Profile} />} />
            <Route path="/user/local-link" element={<PrivateRoute element={Local_Link} />} />
            <Route path="/user/manage" element={<PrivateRoute element={Manage} />} />
            <Route path="/user/calendar" element={<PrivateRoute element={Calendar} />} />
            <Route path="/user/library" element={<PrivateRoute element={Library} />} />
            <Route path="/user/reservations" element={<PrivateRoute element={Reservations} />} />
            <Route path="/user/pet/registry" element={<PrivateRoute element={PetRegistry} />} />
            <Route path="/user/front-desk" element={<PrivateRoute element={FrontDesk} />} />
            <Route path="/user/event/log" element={<PrivateRoute element={EventLog} />} />
            <Route path="/user/instructions" element={<PrivateRoute element={Instructions} />} />
            <Route path="/user/incidents" element={<PrivateRoute element={Incidents} />} />
            <Route path="/user/resident/directory" element={<PrivateRoute element={Resident_Directory} />} />
            <Route path="/user/maintenance" element={<PrivateRoute element={Maintenance} />} />
            <Route path="/user/maintenance/new/request" element={<PrivateRoute element={Maintenance} />} />
            <Route path="/user/maintenance/search/requests" element={<PrivateRoute element={searchRequests} />} />
            <Route path="/user/maintenance/vendor/directory" element={<PrivateRoute element={Tables} />} />
            <Route path="/user/communicate" element={<PrivateRoute element={Communicate} />} />
            <Route path="/user/communicate/send-email" element={<PrivateRoute element={Email} />} />
            <Route path="/user/resident-site" element={<PrivateRoute element={ResidentSite} />} />
            <Route path="/user/other" element={<PrivateRoute element={Other} />} />
            <Route path="/user/property" element={<PrivateRoute element={Property} />} /> */}
          </>
        )}

        {/* ============ CATCH-ALL ============ */}
        <Route path="*" element={<Navigate to={getDefaultPath()} replace />} />
      </Routes>
    </>
  );
}

export default App;